import { Button, DatePicker, FormField, Modal } from '@dev-spendesk/grapes';
import { min } from 'date-fns';
import { useState } from 'react';

import PlaceAutocomplete from 'common/components/PlaceAutocomplete/PlaceAutocomplete';
import { type Location } from 'common/components/PlaceAutocomplete/location';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useUser } from 'modules/app/hooks/useUser';
import {
  NotificationType,
  useNotifications,
} from 'modules/app/notifications/hooks/useNotifications';
import {
  MAX_SEARCH_DATE,
  MIN_SEARCH_DATE,
} from 'modules/trips/components/StaysSearch/StaysSearch';
import {
  transformToPayload,
  usePropertiesSearchMutation,
} from 'modules/trips/hooks/usePropertiesSearchMutation';
import { type TravelSearchState } from 'modules/trips/models/TravelSearchState';
import { routeFor, routes } from 'src/core/constants/routes';
import history from 'src/core/history';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  initialState?: TravelSearchState;
};

export function StaysSearchEditModal({ isOpen, onClose, initialState }: Props) {
  const { t } = useTranslation('global');
  const company = useCompany();
  const user = useUser();
  const propertiesSearchMutation = usePropertiesSearchMutation();
  const { pushNotif } = useNotifications();

  const [location, setLocation] = useState<Location | undefined>(
    initialState?.location,
  );
  const [checkin, setCheckin] = useState<Date | undefined>(
    initialState?.checkin,
  );
  const [checkout, setCheckout] = useState<Date | undefined>(
    initialState?.checkout,
  );

  const handleSubmit = async () => {
    if (!location || !checkin || !checkout) {
      return;
    }
    const response = await propertiesSearchMutation.mutateAsync(
      transformToPayload(location, checkin, checkout),
    );
    if (response.statusCode === 201) {
      history.push(
        routeFor(routes.TRAVEL_SEARCH.path, {
          company: company.id,
        }),
        {
          location,
          checkin,
          checkout,
          searchId: response.body.data.sessionId,
        },
      );
      onClose();
    } else {
      pushNotif({
        type: NotificationType.Danger,
        message: t('misc.errors.unknownError'),
      });
    }
  };

  return (
    <Modal
      iconName="bed"
      isOpen={isOpen}
      onClose={onClose}
      title={t('travel.hotel.search.editSearch')}
      actions={[
        <Button
          key="cancel"
          variant="secondaryNeutral"
          text={t('misc.cancel')}
          onClick={onClose}
        />,
        <Button
          key="search"
          variant="primaryBrand"
          text={t('travel.hotel.search.searchButton')}
          onClick={handleSubmit}
          isDisabled={
            !location ||
            !checkin ||
            !checkout ||
            propertiesSearchMutation.isLoading
          }
          isLoading={propertiesSearchMutation.isLoading}
        />,
      ]}
    >
      <form className="body-m flex flex-col gap-16">
        <FormField label={t('travel.hotel.search.place')}>
          <PlaceAutocomplete
            onPlaceSelect={(newLocation) => {
              setLocation(newLocation ?? undefined);
            }}
            initialInputValue={location?.name}
            placeholder={t('travel.hotel.search.placePlaceholder')}
            language={user.lang}
            requesterCountryCode={company.country}
          />
        </FormField>
        <div className="flex gap-16">
          <FormField
            label={t('travel.hotel.search.checkin')}
            className="flex-1"
          >
            <DatePicker
              fit="parent"
              minDate={MIN_SEARCH_DATE}
              maxDate={
                checkout ? min([checkout, MAX_SEARCH_DATE]) : MAX_SEARCH_DATE
              }
              value={checkin}
              onChange={(value) => setCheckin(value)}
            />
          </FormField>
          <FormField
            label={t('travel.hotel.search.checkout')}
            className="flex-1"
          >
            <DatePicker
              fit="parent"
              placement="bottom-end"
              minDate={checkin}
              maxDate={MAX_SEARCH_DATE}
              value={checkout}
              onChange={(value) => setCheckout(value)}
            />
          </FormField>
        </div>
      </form>
    </Modal>
  );
}
